import { HeroIcon } from '@components/HeroIcon'
import { Modal } from '@components/Modal'
import { ProgressCircle } from '@components/ProgressCircle'
import { ScrollBar } from '@components/ScrollBar'
import { ContentsModalProvider } from '@contexts/ContentsModalContext'
import { useCourse } from '@hooks/useCourse'
import { useEnrollment } from '@hooks/useEnrollment'
import { useModules } from '@hooks/useModules'
import { useContentsModalStore } from '@stores/useContentsModalStore'
import { useLocalSearchParams, usePathname } from 'expo-router'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Text, XStack, YStack } from 'tamagui'
import { ContentsModules } from './ContentsModules'
import { useFullScreenModeStore } from '@stores/useFullScreenModeStore'
import { REDIRECT_TO_CONTENT_TYPES } from '@utils/productRedirectToEnrollment'
import { ICourseModulesNodes } from '@dtos/Course'

export default function ContentsModal() {
  const { position, setPosition } = useContentsModalStore()
  const { modules, onLoadMore, onLoadMoreSubmodules, hasMoreSubmodules } =
    useModules()
  const { enrollment } = useEnrollment()
  const { course } = useCourse()
  const { moduleId, parentCourseModuleId } = useLocalSearchParams()
  const [scrollBarHeight, setScrollBarHeight] = useState(0)
  const { isFullScreenMode } = useFullScreenModeStore()
  const actualPath = usePathname()
  const snapPoints = [85, 15]
  const isModalOpen = position === 0
  const hasBeenOpenedRef = useRef(false)

  useEffect(() => {
    if (isModalOpen) {
      hasBeenOpenedRef.current = true
    }
  }, [isModalOpen])
  const modalChevronIcon = isModalOpen ? 'xmark' : 'chevron-up'

  const handleChange = useCallback(
    (index: number) => {
      setPosition(index)
    },
    [setPosition],
  )

  const togglePosition = useCallback(() => {
    setPosition(position === 0 ? 1 : 0)
  }, [position, setPosition])

  const setIsOpen = useCallback(
    (isOpen: number) => {
      setPosition(isOpen)
    },
    [setPosition],
  )

  const hideModalOnSingleContentCouseType = useMemo(() => {
    if (!course) return false
    return REDIRECT_TO_CONTENT_TYPES.includes(course.kind)
  }, [course])

  const openModal = useMemo(() => {
    return !isFullScreenMode
  }, [isFullScreenMode])

  const filteredModules = useMemo(() => {
    if (!modules) return []
    return modules.nodes.filter((module) => {
      const isModuleAvailable = module.available !== false
      const hasCourseContents = module.courseContents.nodes.length > 0
      const submodulesHasCourseContents = !!module.courseModules.some(
        (submodule: ICourseModulesNodes) =>
          submodule.courseContents.nodes.length > 0,
      )
      const hasCourseContentsOrSubmodules =
        hasCourseContents || submodulesHasCourseContents
      return isModuleAvailable && hasCourseContentsOrSubmodules
    })
  }, [modules])

  const renderModules = useMemo(() => {
    if (!hasBeenOpenedRef.current && !isModalOpen) {
      return null
    }
    return filteredModules.map((module, index) => {
      const isModuleOpen = [moduleId, parentCourseModuleId].includes(
        module.id.toString(),
      )
      const isLastModule = index === filteredModules.length - 1
      return (
        <React.Fragment key={module.id}>
          <ContentsModules
            module={module}
            enrollment={enrollment}
            course={course}
            moduleId={moduleId}
            onLoadMore={onLoadMore}
            onLoadMoreSubmodules={onLoadMoreSubmodules}
            hasMoreSubmodules={hasMoreSubmodules}
            isLastModule={isLastModule}
            isModuleOpen={isModuleOpen}
            isSubmodule={false}
          />
        </React.Fragment>
      )
    })
  }, [
    filteredModules,
    enrollment,
    course,
    moduleId,
    parentCourseModuleId,
    onLoadMore,
    onLoadMoreSubmodules,
    hasMoreSubmodules,
    isModalOpen,
  ])

  const isValidPath = useMemo(() => {
    return /^\/enrollments\/\d+\/courses\/\d+\/online_course\/course_contents\/\d+$/.test(
      actualPath,
    )
  }, [actualPath])

  const displayModal = useMemo(() => {
    return isValidPath && openModal && !hideModalOnSingleContentCouseType
  }, [isValidPath, openModal, hideModalOnSingleContentCouseType])

  const enrollmentProgress = useMemo(() => {
    return enrollment?.progressCard?.progress || 0
  }, [enrollment?.progressCard?.progress])

  return (
    <>
      <ContentsModalProvider setIsOpen={setIsOpen} isOpen={isModalOpen}>
        <Modal
          open={displayModal}
          defaultOpen={displayModal}
          snapPoints={snapPoints}
          position={position}
          shouldAdapt={false}
          onPositionChange={handleChange}
          keepVisible={displayModal}
          borderRadius="$border-radius-xlg"
          disableDrag
          hideOverlay
          withBorder
          zIndex={10}
          whenShouldAdapt="md"
        >
          <XStack
            p="$spacing-inset-xs"
            gap="$spacing-inset-nano"
            alignItems="center"
            minHeight={100}
            onPress={togglePosition}
            role="button"
          >
            <ProgressCircle progress={enrollmentProgress} />
            <YStack flex={1} gap="$spacing-inset-quarck">
              <Text
                color="$text-primary"
                fontSize="$font-size-xs"
                fontFamily="$mulishSemiBold"
                fontWeight="600"
                width="90%"
                numberOfLines={3}
              >
                {course?.name}
              </Text>
              <XStack gap="$spacing-inset-quarck">
                <Text
                  color="$progress-complete"
                  fontSize="$font-size-xxxs"
                  fontFamily="$mulishBold"
                  fontWeight="700"
                >
                  {Math.floor(enrollment?.progressCard?.progress || 0)}%
                </Text>
                <Text
                  color="$text-primary"
                  fontSize="$font-size-xxxs"
                  fontFamily="$mulishBold"
                  fontWeight="700"
                >
                  |
                </Text>
                <Text
                  color="$progress-complete"
                  fontSize="$font-size-xxxs"
                  fontFamily="$mulishBold"
                  fontWeight="700"
                >
                  {enrollment?.progressCard?.completed}
                </Text>
                <Text
                  color="$text-primary"
                  fontSize="$font-size-xxxs"
                  fontFamily="$mulishRegular"
                >
                  de {enrollment?.progressCard?.total} conteúdos
                </Text>
              </XStack>
            </YStack>
            <HeroIcon
              icon={['fal', modalChevronIcon]}
              size={16}
              color="$text-muted"
            />
          </XStack>
          <YStack
            flex={1}
            backgroundColor="$bg-surface"
            paddingVertical="$spacing-inset-xs"
            marginHorizontal="$spacing-inset-xs"
            onLayout={(event) =>
              setScrollBarHeight(event.nativeEvent.layout.height)
            }
          >
            <ScrollBar
              maxHeight={scrollBarHeight}
              displayNativeIndicator={false}
              scrollBarId="contents-modal-scroll"
            >
              {renderModules}
            </ScrollBar>
          </YStack>
        </Modal>
      </ContentsModalProvider>
    </>
  )
}
